import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from 'src/app/core/services/auth.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-qr-redirect',
  templateUrl: './qr-redirect.component.html',
  styleUrls: ['./qr-redirect.component.css'],
})
export class QrRedirectComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly APP_OPEN_TIMEOUT = 3000; // 3 seconds - increased for iOS Chrome
  private readonly DEEP_LINK_BASE = 'technology.jk.jujuja://stores';
  private readonly IOS_APP_STORE_URL =
    'https://apps.apple.com/app/jujuja/id6553972212';
  private readonly ANDROID_APP_STORE_URL =
    'https://play.google.com/store/apps/details?id=technology.jk.jujuja';

  storeId: string = '';
  isAttemptingAppOpen = true;
  isMobileDevice = false;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly authService: AuthService
  ) {}

  ngOnInit(): void {
    // Get storeId from query params
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        this.storeId = params['storeId'] || params['booking'] || '';
        if (!this.storeId) {
          // No storeId, redirect to error or home
          this.redirectToCheckin();
          return;
        }

        // Immediately attempt to open app if on mobile device
        // Only show page content if app doesn't open
        this.detectAndHandleDevice();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private detectAndHandleDevice(): void {
    const userAgent = navigator.userAgent || navigator.vendor;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);
    const isAndroid = /Android/i.test(userAgent);
    this.isMobileDevice = isIOS || isAndroid;

    if (this.isMobileDevice) {
      // Try to open app immediately - page will only show if app doesn't open
      this.attemptOpenApp();
    } else {
      // Desktop device - directly redirect to checkin
      this.redirectToCheckin();
    }
  }

  private attemptOpenApp(): void {
    const userAgent = navigator.userAgent || navigator.vendor;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);
    const isAndroid = /Android/i.test(userAgent);
    const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);

    // Build deep link with storeId
    const deepLink = `${this.DEEP_LINK_BASE}?storeId=${this.storeId}`;
    let appOpened = false;
    let timeoutId: any;
    const startTime = Date.now();
    let wasPageVisible = !document.hidden;

    // Track if page becomes hidden (indicates app opened)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page became hidden - app likely opened
        appOpened = true;
        clearTimeout(timeoutId);
      } else {
        // Page became visible again
        const elapsed = Date.now() - startTime;
        // If page becomes visible again quickly (< 500ms), app probably didn't open
        // But if enough time passed, app likely opened and user came back
        if (elapsed < 500 && wasPageVisible) {
          // Page became visible again quickly - app probably didn't open
          // But don't set appOpened to false, let timeout handle it
        }
      }
    };

    const handleBlur = () => {
      appOpened = true;
      clearTimeout(timeoutId);
    };

    const handlePageHide = () => {
      appOpened = true;
      clearTimeout(timeoutId);
    };

    // For Safari, also listen for focus events (when user returns to page)
    const handleFocus = () => {
      const elapsed = Date.now() - startTime;
      // If user returns to page after a delay, app likely opened
      if (elapsed > 1000) {
        appOpened = true;
        clearTimeout(timeoutId);
      }
    };

    // Add multiple event listeners for better detection
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('pagehide', handlePageHide);
    if (isSafari) {
      window.addEventListener('focus', handleFocus);
    }

    if (isIOS) {
      this.openIOSApp(deepLink);
    } else if (isAndroid) {
      this.openAndroidApp(deepLink, userAgent);
    }

    // Set timeout to redirect if app doesn't open
    // Use shorter timeout for Safari since window.location.href is more immediate
    const timeout = isSafari ? 2500 : this.APP_OPEN_TIMEOUT;
    
    timeoutId = setTimeout(() => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('pagehide', handlePageHide);
      if (isSafari) {
        window.removeEventListener('focus', handleFocus);
      }

      // Double check if page is still visible (app didn't open)
      if (!appOpened && !document.hidden) {
        // App didn't open - show the qr-redirect page content
        this.isAttemptingAppOpen = false;
        // Don't redirect to checkin immediately - let user see the page
        // They can manually choose to go to checkin or download app
      }
    }, timeout);
  }

  private openIOSApp(deepLink: string): void {
    // iOS - try multiple methods for better compatibility
    const userAgent = navigator.userAgent || navigator.vendor;
    const isChrome = /CriOS|Chrome/.test(userAgent);
    const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);

    if (isChrome) {
      // For Chrome on iOS, use window.location.href directly
      // Chrome on iOS handles this better than iframe
      window.location.href = deepLink;
    } else if (isSafari) {
      // For Safari, use window.location.href (works better than iframe in modern Safari)
      // Safari will attempt to open the app, and if it fails, we'll detect it via visibility change
      try {
        window.location.href = deepLink;
      } catch (e) {
        console.error('Error opening deep link in Safari:', e);
        // Fallback to iframe method if window.location fails
        this.openIOSAppWithIframe(deepLink);
      }
    } else {
      // For other browsers, try window.location first
      window.location.href = deepLink;
    }
  }

  private openIOSAppWithIframe(deepLink: string): void {
    // iOS - use iframe method as fallback
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.style.width = '1px';
    iframe.style.height = '1px';
    iframe.style.position = 'absolute';
    iframe.style.top = '-1000px';
    iframe.src = deepLink;
    document.body.appendChild(iframe);

    setTimeout(() => {
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe);
      }
    }, 1000);
  }

  private openAndroidApp(deepLink: string, userAgent: string): void {
    const isChrome = /Chrome/.test(userAgent);

    if (isChrome) {
      // Android Chrome - use intent URL
      const intentUrl = `intent://stores?storeId=${this.storeId}#Intent;scheme=technology.jk.jujuja;package=technology.jk.jujuja;end`;
      window.location.href = intentUrl;
    } else {
      // Other Android browsers
      window.location.href = deepLink;
    }
  }

  private redirectToCheckin(): void {
    const queryParams = this.storeId ? { storeId: this.storeId } : {};
    this.router.navigate(['/checkin'], { queryParams });
  }

  // Fallback method if user wants to manually go to checkin
  goToCheckin(): void {
    this.redirectToCheckin();
  }

  downloadApp(): void {
    const userAgent = navigator.userAgent || navigator.vendor;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);
    const isAndroid = /Android/i.test(userAgent);

    if (isIOS) {
      window.open(this.IOS_APP_STORE_URL, '_blank');
    } else if (isAndroid) {
      window.open(this.ANDROID_APP_STORE_URL, '_blank');
    }
  }
}

